import { useState } from "react";
import { api, getApiBase, authToken } from "@/api";
import { toast } from "@/lib/toast";
import { Database, Download, Upload, AlertTriangle } from "lucide-react";

export default function LegacyJsonBackup() {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.get("/backup/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `dgfiscalflow_backup_${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Backup généré (${r.data.total_documents} document(s))`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!window.confirm(`Importer ${file.name} ? Les données existantes avec le même identifiant seront mises à jour.`)) {
      event.target.value = "";
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Use fetch directly to send multipart (avoids axios JSON header)
      const resp = await fetch(`${getApiBase()}/backup/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken()}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Erreur import");
      toast.success(`Import OK: ${data.inserted} ajoutés, ${data.updated} mis à jour`);
    } catch (e) {
      toast.error(e.message || "Erreur lors de l'import");
    } finally {
      setImporting(false);
      event.target.value = "";
    }
  };

  return (
    <details className="group" data-testid="backup-legacy">
      <summary className="cursor-pointer text-sm font-semibold text-gray-600 flex items-center gap-2 select-none">
        <Database className="w-4 h-4 text-slate-400" /> Ancien export / import JSON (partiel — clients, factures, dépenses, modèles)
      </summary>
      <div className="mt-3 space-y-4">

      <div className="bg-white border rounded-xl p-5 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Exporter (sauvegarde complète)</h3>
          <p className="text-xs text-gray-500 mb-3">
            Télécharge un fichier JSON contenant toutes les données. À conserver en lieu sûr.
          </p>
          <button onClick={handleExport} disabled={exporting}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-indigo-700 disabled:opacity-50"
            data-testid="backup-export-btn">
            <Download className="w-4 h-4" />
            {exporting ? "Génération..." : "Exporter les données"}
          </button>
        </div>

        <div className="border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Importer (restauration)</h3>
          <p className="text-xs text-gray-500 mb-3">
            Charge un fichier JSON précédemment exporté. Les enregistrements avec le même identifiant
            sont mis à jour.
          </p>
          <label className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm cursor-pointer hover:bg-emerald-700"
            data-testid="backup-import-label">
            <Upload className="w-4 h-4" />
            {importing ? "Import en cours..." : "Importer un fichier JSON"}
            <input type="file" accept=".json"
              onChange={handleImport} disabled={importing} className="hidden"
              data-testid="backup-import-input" />
          </label>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Important :</strong> l'import remplace les enregistrements ayant le même
            identifiant. Faites un export de précaution avant d'importer.
          </div>
        </div>
      </div>
      </div>
    </details>
  );
}
